/**
 * Thin wrapper around posthog-js so:
 *   - PostHog stays optional (no REACT_APP_POSTHOG_KEY → fully inert)
 *   - Wallet addresses get hashed before they leave the browser
 *   - URLs/referrers with embedded wallet/token/network addresses get
 *     stripped so $pageview events don't carry raw addresses
 *   - identify/reset on login/logout funnel through one place
 *
 * Called from src/utils/analytics.ts (event dual-write) and AuthContext
 * (identify/reset). Do NOT import posthog-js directly elsewhere — all
 * captures should go through trackEvent so the same sanitization rules
 * apply to every event.
 */
import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;

// In production, route ingestion through our own domain (Netlify rewrite
// at /_phr/* → us.i.posthog.com) so ad-blockers that match the PostHog
// hosts don't drop our analytics. `ui_host` keeps dashboard links
// pointing at the real PostHog UI. Env var override exists so dev/
// staging can hit PostHog directly without setting up a proxy.
const POSTHOG_API_HOST = process.env.REACT_APP_POSTHOG_HOST
  || (typeof window !== 'undefined' ? `${window.location.origin}/_phr` : 'https://us.i.posthog.com');
const POSTHOG_UI_HOST = 'https://us.posthog.com';

// Build id, inlined at compile time by config-overrides.js (webpack DefinePlugin)
// from the same value gen-version.js writes to /version.json. Available before any
// event fires, so it can be stamped synchronously (see sanitizeUrlProperties) —
// unlike the old runtime fetch, which left early events untagged. `typeof` guard
// keeps it safe under jest, where DefinePlugin doesn't run.
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '';

let initialized = false;

export function initPostHog(): void {
  if (initialized) return;
  if (!POSTHOG_KEY) return;
  if (typeof window === 'undefined') return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_API_HOST,
    ui_host: POSTHOG_UI_HOST,
    // 'always' (not 'identified_only') so anonymous visitors are counted in
    // Web Analytics. With identified_only, the Visitor count collapsed to
    // just the handful of authenticated users — single-digit numbers vs
    // Netlify's server-side count. No PII change: anon users get a random
    // distinct_id cookie either way; identify() upgrades to user.id on
    // sign-in.
    person_profiles: 'always',
    capture_pageview: 'history_change',
    autocapture: true,
    // Error tracking: autocapture unhandled exceptions + promise rejections as
    // $exception events. Caught errors (e.g. a failed checkout fetch) never reach
    // the global handler, so those are reported explicitly via captureError().
    capture_exceptions: true,
    sanitize_properties: sanitizeUrlProperties,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-mask], .wallet-address, .token-mint',
    },
  });
  initialized = true;
}

/**
 * Stable, non-reversible 12-char fingerprint of a wallet address.
 * Same wallet → same fingerprint across sessions, so funnel queries
 * can still answer "did this wallet come back" without PostHog ever
 * seeing the raw address.
 */
async function hashWallet(addr: string): Promise<string> {
  const bytes = new TextEncoder().encode(`deepnets:${addr}`);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .slice(0, 6)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function identifyUser(user: {
  id: string;
  walletAddress: string | null;
  tier?: string;
  dnetTier?: string;
  dnetBalance?: number;
}): Promise<void> {
  if (!initialized) return;
  const walletFingerprint = user.walletAddress
    ? await hashWallet(user.walletAddress)
    : undefined;
  posthog.identify(user.id, {
    walletFingerprint,
    tier: user.tier,
    dnetTier: user.dnetTier,
    dnetBalance: user.dnetBalance,
  });
}

export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
}

export function posthogCapture(name: string, props: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(name, sanitizeEventProps(props));
}

/**
 * Report a caught error to PostHog as a $exception event (with stack + grouping).
 * Use for errors that are try/caught and so never reach the global handler —
 * e.g. a failed checkout fetch. `context` props are attached (sanitized) so we
 * can tell which flow failed. No-op when PostHog isn't configured.
 */
export function captureError(error: unknown, context: Record<string, unknown> = {}): void {
  if (!initialized) return;
  const err = error instanceof Error
    ? error
    : new Error(typeof error === 'string' ? error : (() => { try { return JSON.stringify(error); } catch { return String(error); } })());
  posthog.captureException(err, sanitizeEventProps(context));
}

function sanitizeEventProps(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    // Server-side analytics_events keeps the raw address; PostHog only
    // needs "same user" identity, which identify() already provides
    // via the wallet fingerprint.
    if (k === 'walletAddress' || k === 'wallet') continue;
    out[k] = v;
  }
  return out;
}

function sanitizeUrlProperties(properties: Record<string, any>): Record<string, any> {
  // Stamp the running build on every event (this hook is posthog's global
  // sanitize_properties, so it runs for autocapture/$pageview/$exception too —
  // 100% coverage, including the initial pageview fired inside posthog.init).
  //
  // We intentionally send the REAL URL (mint/address unmasked). Grouping the
  // parameterized routes (/token/:mint, /wallet/:address, …) for aggregate
  // stats is done with PostHog Path Cleaning Rules (Project Settings), which
  // collapse them in Web Analytics / path breakdowns while keeping the raw URL
  // for per-mint drill-down. Masking here would throw the mint away entirely.
  if (APP_VERSION) properties.app_version = APP_VERSION;
  return properties;
}
